<template>
    <div class="picker passenger-picker">
        <input-button
            :class="'rounded passenger-picker-btn'
                + ((passengerError !== null || railcardsRequired === -1) ? ' error' : '')" type="outline"
            icon="none" @click="showPassengerPicker" @click-inner="didClickAuxiliary"
        >
            <span>
                {{ buttonText }}</span><font-awesome-icon
                :icon="'fa-solid ' + (isOpen ? 'fa-caret-up' : 'fa-caret-down')" class="ml-1"
            ></font-awesome-icon>
        </input-button>
        <div v-if="isOpen" class="block-ui" @click="closePicker"></div>
        <div v-show="isOpen" class="background-blocker show-when-modal"></div>
        <div v-show="isOpen" class="picker-dropdown rounded shadow">
            <div class="dropdown-header show-when-modal">
                <slot>Passengers</slot>
            </div>
            <div class="passenger-picker">
                <options-group
                    v-if="modifiedOptions" v-model="modifiedOptions" :config="passengerOptionsConfig"
                    @input="validate"
                ></options-group>
            </div>
            <div class="action-buttons">
                <button class="btn btn-primary" @click="closePicker">
                    Close
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import * as Sentry from '@sentry/vue';
    import { PassengerStringUtils } from '@raileasyuk/ts-frontend-utils';
    import firebase from 'firebase/app';
    import { DateTime } from 'luxon';
    import InputButton from './InputButton';
    import OptionsGroup from './OptionsGroup';
    import PurchasedRailcards from '../utilities/PurchasedRailcards';

    export default {
        name: 'PassengerPicker',
        components: {
            InputButton,
            OptionsGroup,
        },
        props: {
            railcards: {
                type: Array,
                default() {
                    return [];
                },
            },
            value: {
                type: Object,
                default() {
                    return {
                        adults: 1,
                        children: 0,
                        railcard: 'none',
                    };
                },
            },
        },
        data() {
            return {
                isOpen: false,
                options: {
                    passengers: {
                        adults: 1,
                        children: 0,
                    },
                    railcard: '',
                },
                purchasedRailcards: [],
                modifiedOptions: null,
                passengerError: null,
                railcardsRequired: 0,
                isLoadingRailcardValidity: false,
            };
        },
        computed: {
            passengerOptionsConfig() {
                return {
                    options: {
                        passengers: {
                            label: 'Passengers',
                            type: 'DropdownList',
                            description: 'Children under 5 travel free, unless they need a seat reservation',
                            options: {
                                adults: this.optionList(9, 'Adult'),
                                children: this.optionList(9, 'Child', 'ren'),
                            },
                            error: this.passengerError,
                        },
                        railcard: {
                            label: 'Railcard',
                            description: 'Must apply to all passengers',
                            type: 'DropdownList',
                            options: [{
                                label: 'No Railcard',
                                value: 'none',
                                group: '',
                            }].concat(this.purchasedRailcards).concat(this.railcards),
                            nullValue: 'none',
                            warning: this.railcardsRequired > 1 ? `You will need ${this.railcardsRequired} railcards.`
                                + ' If you don\'t have enough your tickets won\'t be valid.' : null,
                            error: this.railcardsRequired === -1 ? 'Your railcard isn\'t valid for '
                                + ' this combination of adults and children. Please add non-railcard holders as separate '
                                + 'journeys into your basket so you can pay for them together.' : null,
                        },
                    },
                };
            },
            labelForRailcard() {
                const config = this.passengerOptionsConfig.options.railcard;
                if (!config) {
                    return '';
                }
                const value = this.options.railcard;
                const {
                    options,
                } = config;
                const matching = options.filter((option) => option.value === value);
                return matching.length > 0 ? matching[0].label : 'No Railcard';
            },
            buttonText() {
                const { passengers } = this.options;
                const numChildren = parseInt(passengers.children, 10);
                const numAdults = parseInt(passengers.adults, 10);

                const combinedPax = PassengerStringUtils.personText(numAdults, numChildren);
                return `${combinedPax}${this.labelForRailcard ? `, ${this.labelForRailcard}` : ''}`;
            },
            isValid() {
                return !this.isLoadingRailcardValidity && this.passengerError === null && this.railcardsRequired !== -1;
            },
        },
        watch: {
            value: {
                handler() {
                    this.updateValues();
                },
                deep: true,
            },
        },
        created() {
            // produce a map of railcard codes (property "value") to labels (property "label") by looping over this.railcards
            const railcardMap = this.railcards.reduce((map, railcard) => {
                // eslint-disable-next-line no-param-reassign
                map[railcard.value] = railcard.label;
                return map;
            }, {});

            firebase.auth().onAuthStateChanged((user) => {
                if (user) {
                    const purchasedRailcards = PurchasedRailcards.getPurchasedRailcardsFromCacheOrRemote();
                    purchasedRailcards.then((pr) => {
                        this.purchasedRailcards = pr.map((railcard) => {
                            const expiry = DateTime.fromISO(railcard.dateTo).toFormat('dd/MM/yyyy');

                            return {
                                label: `${railcardMap[railcard.type]}`,
                                dropdownLabel: `${railcardMap[railcard.type]} (expires ${expiry})`,
                                value: railcard.type,
                                group: 'Your Railcards',
                            };
                        });
                    });
                }
            });
        },
        methods: {
            optionList(max, unit, optionalPlural) {
                const plural = optionalPlural || 's';
                const list = [];
                for (let value = 0; value <= max; value++) {
                    const labelValue = value === 0 ? 'No' : value;
                    const labelUnit = value === 1 ? unit : `${unit}${plural}`;
                    list.push({
                        value,
                        label: `${labelValue} ${labelUnit}`,
                    });
                }
                return list;
            },
            didClickAuxiliary() {
                this.showPassengerPicker();
            },
            updateValues() {
                this.options.passengers.adults = this.value.adults;
                this.options.passengers.children = this.value.children;
                this.options.railcard = this.value.railcard;
            },
            showPassengerPicker() {
                this.updateValues();
                this.modifiedOptions = this.options;
                this.isOpen = true;
            },
            closePicker() {
                this.isOpen = false;
            },
            syncPassengersRailcard() {
                this.$emit('input', {
                    adults: this.options.passengers.adults,
                    children: this.options.passengers.children,
                    railcard: this.options.railcard,
                    railcardsRequired: this.railcardsRequired,
                });
            },
            generateList(from, to, step) {
                const list = [];
                for (let x = from; x <= to; x += step) {
                    list.push(`0${x}`.slice(-2));
                }
                return list;
            },
            validate() {
                this.validateRailcard();
                this.validatePassengers();
            },
            validateRailcard() {
                if (this.modifiedOptions.railcard === 'none') {
                    this.railcardsRequired = 0;
                    this.syncPassengersRailcard();
                    return;
                }

                const request = {
                    adults: this.modifiedOptions.passengers.adults,
                    children: this.modifiedOptions.passengers.children,
                    railcard: this.modifiedOptions.railcard,
                };
                window.progressIndicator.start();
                this.isLoadingRailcardValidity = true;

                fetch('/validateRailcard', {
                    method: 'POST',
                    body: JSON.stringify(request),
                })
                    .then((response) => response.json())
                    .then((json) => {
                        if (json.result === 0) {
                            // The API returns 0 if the passenger/railcard combination is invalid.
                            this.railcardsRequired = -1;
                        } else {
                            this.railcardsRequired = json.result;
                        }
                        this.isLoadingRailcardValidity = false;
                        window.progressIndicator.stop();
                        this.syncPassengersRailcard();
                    })
                    .catch((e) => {
                        window.progressIndicator.stop();
                        this.isLoadingRailcardValidity = false;
                        Sentry.captureException(e);
                    });
            },
            validatePassengers() {
                const adults = parseInt(this.modifiedOptions.passengers.adults, 10);
                const children = parseInt(this.modifiedOptions.passengers.children, 10);
                if (adults + children === 0) {
                    this.passengerError = 'You need to select at least one passenger.';
                    return;
                }

                if (adults + children > 9) {
                    this.passengerError = 'You cannot have more than 9 passengers. If you need more, add them as separate '
                        + 'journeys in your basket so you can pay for them together.';
                    return;
                }

                this.passengerError = null;
                this.$emit('input', {
                    adults: this.options.passengers.adults,
                    children: this.options.passengers.children,
                    railcard: this.options.railcard,
                    railcardsRequired: this.railcardsRequired,
                });
            },
        },
    };
</script>S
